import React, { useState } from "react";
import { SaveAll } from "lucide-react";
import {
  Menubar,
  MenubarMenu,
  MenubarTrigger,
} from "@/components/ui/menubar";
import { 
  getSavedJourneys, 
  getFolders,
  createFolder,
  updateFolder,
  deleteFolder,
  saveCurrentJourney,
  moveJourneyToFolder,
  loadJourney,
  deleteJourney
} from "@/utils/storage";
import { Stage, UserJourney } from "@/types/journey";
import FolderDialogs from "./FolderDialogs";
import SaveDialog from "./SaveDialog";
import NotebookMenuContent from "./NotebookMenuContent";
import BuyMeCoffeeButton from "@/components/BuyMeCoffeeButton";

interface NotebookMenuProps {
  onSave: () => void;
  setStages: React.Dispatch<React.SetStateAction<Stage[]>>;
  setJourney: React.Dispatch<React.SetStateAction<UserJourney>>;
  stages: Stage[];
  journey: UserJourney;
  isMobile?: boolean;
}

const NotebookMenu: React.FC<NotebookMenuProps> = ({ onSave, setStages, setJourney, stages, journey, isMobile = false }) => {
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);
  const [isRenameFolderOpen, setIsRenameFolderOpen] = useState(false);
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  
  const savedJourneys = getSavedJourneys();
  const folders = getFolders();
  
  const handleLoad = (id: string) => {
    loadJourney(id, setStages, setJourney);
    forceRefresh();
  };
  
  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (deleteJourney(id)) {
      forceRefresh();
    }
  };
  
  const handleCreateFolder = () => {
    if (newFolderName.trim()) {
      createFolder(newFolderName);
      setNewFolderName("");
      setIsCreateFolderOpen(false);
      forceRefresh();
    }
  };
  
  const handleRenameFolder = () => {
    if (newFolderName.trim() && selectedFolderId) {
      updateFolder(selectedFolderId, newFolderName);
      setNewFolderName("");
      setIsRenameFolderOpen(false);
      forceRefresh();
    }
  };
  
  const handleDeleteFolder = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (deleteFolder(id)) {
      forceRefresh();
    }
  };
  
  const handleSaveToFolder = (folderId: string | null) => {
    saveCurrentJourney(stages, journey, folderId);
    setIsSaveDialogOpen(false);
    forceRefresh();
  };
  
  const handleMoveToFolder = (journeyId: string, folderId: string | null) => {
    if (moveJourneyToFolder(journeyId, folderId)) {
      forceRefresh();
    }
  };
  
  const openRenameFolder = (e: React.MouseEvent, id: string, name: string) => {
    e.stopPropagation();
    setSelectedFolderId(id);
    setNewFolderName(name);
    setIsRenameFolderOpen(true);
  };
  
  const forceRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };
  
  return (
    <div className="flex items-center">
      <Menubar className="border-none bg-transparent p-0" key={refreshKey}>
        <MenubarMenu>
          <MenubarTrigger className="p-2 hover:bg-blue-200/50 transition-colors cursor-pointer">
            <SaveAll className={isMobile ? "h-4 w-4" : "h-5 w-5"} />
          </MenubarTrigger>
          <NotebookMenuContent
            savedJourneys={savedJourneys}
            folders={folders}
            onCreateFolder={() => setIsCreateFolderOpen(true)}
            onSave={() => setIsSaveDialogOpen(true)}
            onLoad={handleLoad}
            onDelete={handleDelete}
            onMoveToFolder={handleMoveToFolder}
            openRenameFolder={openRenameFolder}
            handleDeleteFolder={handleDeleteFolder}
            handleSaveToFolder={handleSaveToFolder}
          />
        </MenubarMenu>
      </Menubar>
      
      <FolderDialogs
        isCreateFolderOpen={isCreateFolderOpen}
        setIsCreateFolderOpen={setIsCreateFolderOpen}
        isRenameFolderOpen={isRenameFolderOpen}
        setIsRenameFolderOpen={setIsRenameFolderOpen}
        newFolderName={newFolderName}
        setNewFolderName={setNewFolderName}
        handleCreateFolder={handleCreateFolder}
        handleRenameFolder={handleRenameFolder}
      />
      
      <SaveDialog
        isSaveDialogOpen={isSaveDialogOpen}
        setIsSaveDialogOpen={setIsSaveDialogOpen}
        handleSaveToFolder={handleSaveToFolder}
        folders={folders}
      />
    </div>
  );
};

export default NotebookMenu;
